import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { useAxios } from '@/hooks/useAxios';
import { Asset, Risk, Webpage } from '@/types';

interface Capability {
  name: string;
  title?: string;
  description?: string;
  target?: string;
}

interface Command {
  command: string;
  description: string;
  prefix?: string;
  data?: Asset | Risk | Webpage | Capability; // Full object data for entity search
}

const COMMANDS: Command[] = [
  { command: '/query', description: 'Switch to Query mode', prefix: '/' },
  { command: '/agent', description: 'Switch to Agent mode', prefix: '/' },
];

export const useCommandMenu = (
  newMessage: string,
  isPraetorianUser: boolean,
  onModeChange: (mode: 'query' | 'agent') => void,
  onClearInput: () => void,
  onSearch?: (
    type: 'asset' | 'risk' | 'webpage' | 'capability',
    query: string
  ) => void
) => {
  const axios = useAxios();
  const [recentAssets, setRecentAssets] = useState<Asset[]>([]);
  const [recentRisks, setRecentRisks] = useState<Risk[]>([]);
  const [recentWebpages, setRecentWebpages] = useState<Webpage[]>([]);
  const [recentCapabilities, setRecentCapabilities] = useState<Capability[]>(
    []
  );
  const [showCommandMenu, setShowCommandMenu] = useState(false);
  const [menuType, setMenuType] = useState<'command' | 'search'>('command');

  // Detect current word being typed for autocomplete
  const getCurrentWord = () => {
    const cursorPos = newMessage.length; // Assuming cursor is at end
    const textBeforeCursor = newMessage.slice(0, cursorPos);
    const words = textBeforeCursor.split(/\s+/);
    return words[words.length - 1] || '';
  };

  const currentWord = getCurrentWord();
  const [debouncedCurrentWord] = useDebounce(currentWord, 300);

  // Fetch assets when searching (debounced to avoid too many requests)
  useEffect(() => {
    if (debouncedCurrentWord.startsWith('@')) {
      const searchTerm = debouncedCurrentWord.slice(1).trim();

      // Clear old results immediately when search changes
      if (currentWord !== debouncedCurrentWord) {
        setRecentAssets([]);
      }

      const filters: any[] = [];

      // Add status filter first
      filters.push({ field: 'status', operator: '=', value: 'A' });

      // Search only on dns field since that's what we display
      if (searchTerm) {
        filters.push({
          field: 'dns',
          operator: 'CONTAINS',
          value: searchTerm,
        });
      }

      axios
        .post('/my', {
          node: { labels: ['Asset'], filters, relationships: [] },
          limit: 50,
          orderBy: 'dns',
          descending: false,
        })
        .then(response => {
          setRecentAssets(response.data?.assets || []);
        })
        .catch(err => console.error('Failed to fetch assets:', err));
    } else {
      // Clear assets when not searching
      setRecentAssets([]);
    }
  }, [debouncedCurrentWord, currentWord, axios]);

  // Fetch risks when searching (debounced to avoid too many requests)
  useEffect(() => {
    if (debouncedCurrentWord.startsWith('#')) {
      const searchTerm = debouncedCurrentWord.slice(1).trim();

      // Clear old results immediately when search changes
      if (currentWord !== debouncedCurrentWord) {
        setRecentRisks([]);
      }

      const filters: any[] = [];

      // Search only on name field since that's what we display
      if (searchTerm) {
        filters.push({
          field: 'name',
          operator: 'CONTAINS',
          value: searchTerm,
        });
      }

      axios
        .post('/my', {
          node: { labels: ['Risk'], filters, relationships: [] },
          limit: 50,
          orderBy: 'name',
          descending: false,
        })
        .then(response => {
          setRecentRisks(response.data?.risks || []);
        })
        .catch(err => console.error('Failed to fetch risks:', err));
    } else {
      // Clear risks when not searching
      setRecentRisks([]);
    }
  }, [debouncedCurrentWord, currentWord, axios]);

  // Fetch webpages when searching (debounced to avoid too many requests)
  useEffect(() => {
    if (debouncedCurrentWord.startsWith('&')) {
      const searchTerm = debouncedCurrentWord.slice(1).trim();

      // Clear old results immediately when search changes
      if (currentWord !== debouncedCurrentWord) {
        setRecentWebpages([]);
      }

      const filters: any[] = [];

      // Add status filter first
      filters.push({ field: 'status', operator: '=', value: 'A' });

      // Search on url field since that's what we display
      if (searchTerm) {
        filters.push({
          field: 'url',
          operator: 'CONTAINS',
          value: searchTerm,
        });
      }

      axios
        .post('/my', {
          node: { labels: ['Webpage'], filters, relationships: [] },
          limit: 50,
          orderBy: 'url',
          descending: false,
        })
        .then(response => {
          setRecentWebpages(response.data?.webpages || []);
        })
        .catch(err => console.error('Failed to fetch webpages:', err));
    } else {
      // Clear webpages when not searching
      setRecentWebpages([]);
    }
  }, [debouncedCurrentWord, currentWord, axios]);

  // Fetch capabilities when searching (debounced to avoid too many requests)
  useEffect(() => {
    const isModeSwitch =
      debouncedCurrentWord.startsWith('/query') ||
      debouncedCurrentWord.startsWith('/agent');

    if (debouncedCurrentWord.startsWith('/') && !isModeSwitch) {
      const searchTerm = debouncedCurrentWord.slice(1).trim();

      // Clear old results immediately when search changes
      if (currentWord !== debouncedCurrentWord) {
        setRecentCapabilities([]);
      }

      // Use the capabilities API endpoint with query parameters
      const params = new URLSearchParams();
      if (searchTerm) {
        params.set('name', searchTerm);
      }

      axios
        .get(`/capabilities?${params.toString()}`)
        .then(response => {
          // API returns array directly, not wrapped in object
          const capabilities = Array.isArray(response.data)
            ? response.data
            : [];
          setRecentCapabilities(capabilities);
        })
        .catch(err => console.error('Failed to fetch capabilities:', err));
    } else {
      // Clear capabilities when not searching
      setRecentCapabilities([]);
    }
  }, [debouncedCurrentWord, currentWord, axios]);

  // Show command menu when typing special prefixes
  useEffect(() => {
    // Capability search (all users) - show when current word starts with /
    if (currentWord.startsWith('/')) {
      // For Praetorian users, only show mode commands if explicitly typing /q or /a at message start
      // This way /s, /n, /nuclei etc all search capabilities
      const isTypingModeSwitch =
        isPraetorianUser &&
        currentWord === newMessage && // Must be at start of message
        (('/query'.startsWith(newMessage) && newMessage.startsWith('/q')) ||
          ('/agent'.startsWith(newMessage) && newMessage.startsWith('/a')));

      if (isTypingModeSwitch) {
        setShowCommandMenu(true);
        setMenuType('command');
        return;
      }

      // Otherwise show capability search
      setShowCommandMenu(true);
      setMenuType('search');
      return;
    }

    // Entity search (all users) - show when current word starts with @, #, or &
    if (
      currentWord.startsWith('@') ||
      currentWord.startsWith('#') ||
      currentWord.startsWith('&')
    ) {
      setShowCommandMenu(true);
      setMenuType('search');
      return;
    }

    setShowCommandMenu(false);
  }, [newMessage, currentWord, isPraetorianUser]);

  // Get filtered items based on input and menu type
  const getFilteredCommands = (): Command[] => {
    if (menuType === 'search') {
      // Show assets - already filtered by server, no slicing needed
      if (currentWord.startsWith('@')) {
        return recentAssets.map(asset => ({
          command: `@${asset.dns || asset.name}`,
          description: asset.name || asset.identifier || 'Asset',
          data: asset,
        }));
      }
      // Show risks - already filtered by server, no slicing needed
      if (currentWord.startsWith('#')) {
        return recentRisks.map((risk, index) => ({
          command: `#${risk.name}`,
          description: risk.dns || risk.key || `Risk ${index + 1}`,
          data: risk,
        }));
      }
      // Show webpages - already filtered by server, no slicing needed
      if (currentWord.startsWith('&')) {
        return recentWebpages.map(webpage => ({
          command: `&${webpage.url}`,
          description: webpage.source?.join(', ') || webpage.key || 'Webpage',
          data: webpage,
        }));
      }
      // Show capabilities - already filtered by server, no slicing needed
      if (
        currentWord.startsWith('/') &&
        !currentWord.startsWith('/query') &&
        !currentWord.startsWith('/agent')
      ) {
        return recentCapabilities.map(capability => ({
          command: `/${capability.name}`,
          description:
            capability.title ||
            capability.description ||
            `${capability.target || 'Capability'}`,
          data: capability,
        }));
      }
      return [];
    }

    // For commands
    if (!newMessage || newMessage === '/') {
      return COMMANDS;
    }
    return COMMANDS.filter(cmd =>
      cmd.command.startsWith(newMessage.toLowerCase())
    );
  };

  const filteredCommands = getFilteredCommands();

  // Check if message is a command and execute it
  const executeCommandIfMatch = (message: string): boolean => {
    // Mode switch commands (Praetorian only)
    if (isPraetorianUser) {
      if (message === '/agent') {
        onModeChange('agent');
        onClearInput();
        return true;
      }
      if (message === '/query') {
        onModeChange('query');
        onClearInput();
        return true;
      }
    }

    // Entity search (all users)
    if (onSearch) {
      if (message.startsWith('@')) {
        const query = message.slice(1).trim();
        onSearch('asset', query);
        onClearInput();
        return true;
      }
      if (message.startsWith('#')) {
        const query = message.slice(1).trim();
        onSearch('risk', query);
        onClearInput();
        return true;
      }
      if (message.startsWith('&')) {
        const query = message.slice(1).trim();
        onSearch('webpage', query);
        onClearInput();
        return true;
      }
      // Capability search - only if not a mode switch command
      if (
        message.startsWith('/') &&
        message !== '/query' &&
        message !== '/agent'
      ) {
        const query = message.slice(1).trim();
        onSearch('capability', query);
        onClearInput();
        return true;
      }
    }

    return false;
  };

  // Auto-complete if there's exactly one matching command
  const shouldAutoComplete =
    showCommandMenu && filteredCommands.length === 1 && menuType === 'command';

  const selectCommand = (command: string) => {
    setShowCommandMenu(false);
    // Commands don't need special handling - they execute immediately
    executeCommandIfMatch(command);
  };

  const replaceCurrentWord = (
    replacement: string,
    data?: Asset | Risk | Webpage | Capability
  ): string => {
    const cursorPos = newMessage.length;
    const textBeforeCursor = newMessage.slice(0, cursorPos);
    const textAfterCursor = newMessage.slice(cursorPos);
    const words = textBeforeCursor.split(/\s+/);

    // If we have entity data, use @{key} format for precise backend lookup
    if (data && 'key' in data) {
      const entityRef = `@{${data.key}}`;
      words.pop();
      return (
        words.join(' ') +
        (words.length > 0 ? ' ' : '') +
        entityRef +
        textAfterCursor
      );
    }

    // For capabilities, just use the name
    if (data && 'name' in data && !('key' in data)) {
      words.pop();
      return (
        words.join(' ') +
        (words.length > 0 ? ' ' : '') +
        replacement +
        textAfterCursor
      );
    }

    // Replace the last word with the selected item
    words[words.length - 1] = replacement;
    return words.join(' ') + ' ' + textAfterCursor;
  };

  return {
    showCommandMenu,
    filteredCommands,
    executeCommandIfMatch,
    shouldAutoComplete,
    selectCommand,
    replaceCurrentWord,
    menuType,
  };
};
